import * as ApartmentActions from './apartments.actions';

import {createEntityAdapter, EntityAdapter} from '@ngrx/entity';
import {createReducer, on} from '@ngrx/store';
import {StoredComponentHelperService} from '@common/helpers/stored-component.helper.service';
import {ApartmentModel, ApartmentsSearchParams, ApartmentTypeEnum, SectionType} from '@association/_common/models/apartments.models';
import {EntityStateModel} from '@common/models/entity-state.models';
import {SetAllInitialStates} from '@common/store/actions/core.actions';

export const apartmentsFeatureKey = 'apartments';

export function sortApartments(a: ApartmentModel, b: ApartmentModel): number {
    const getOrder = (type: ApartmentTypeEnum) =>
        ({ APARTMENT: 1, APARTMENTS: 2, PRIVATE_BUILDING: 3, PARKING_PLACE: 4, GARAGE: 5, STORAGE: 6, COMMERCIAL: 7, OTHER: 8 }[type] || 8);

    const getSectionOrder = (type: SectionType | undefined) =>
        ({ SECTION: 1, HOUSING: 2, ENTRANCE: 3, PARKING: 4, FRONT: 5, LINE: 6, ROW: 7 }[type as SectionType] || 8);

    const compare = (valA: string | undefined, valB: string | undefined) =>
        (valA || '').localeCompare(valB || '', undefined, { numeric: true });

    const compareWithUndefinedLast = (valA: string | undefined, valB: string | undefined) => {
        if (valA && !valB) return -1;
        if (!valA && valB) return 1;
        return compare(valA, valB);
    };

    const compareSectionTypeWithUndefinedLast = (typeA?: SectionType, typeB?: SectionType) => {
        if (typeA && !typeB) return -1;
        if (!typeA && typeB) return 1;
        return getSectionOrder(typeA) - getSectionOrder(typeB);
    };

    return (
        compareWithUndefinedLast(a.building?.name, b.building?.name) ||
        compareWithUndefinedLast(a.street?.name, b.street?.name) ||
        compareSectionTypeWithUndefinedLast(a.section?.sectionType, b.section?.sectionType) ||
        compareWithUndefinedLast(a.section?.name, b.section?.name) ||
        getOrder(a.type?.type || 'OTHER') - getOrder(b.type?.type || 'OTHER') ||
        compare(a.number, b.number)
    );

    // return (a.sortOrder ?? Infinity) - (b.sortOrder ?? Infinity);
}

export const adapter: EntityAdapter<ApartmentModel> = createEntityAdapter<ApartmentModel>({
    sortComparer: sortApartments
});

export type ApartmentsState = EntityStateModel<ApartmentModel, ApartmentsSearchParams>;

export const initialState: ApartmentsState = adapter.getInitialState({
    listLoading: 'listLoading',
    pagination: {},
    itemsLoading: []
});

export const apartmentsReducer = createReducer(
    initialState,

    on(ApartmentActions.CheckState, (state, action) => {
        if (action.payload.modalView) {
            return adapter.removeAll({
                ...state,
                savedFilter: !action.payload.close && state.filter ? state.filter : undefined,
                pagination: initialState.pagination,
                filter: action.payload.close ? state.savedFilter : undefined
            });
        } else {
            if (action.payload.close) {
                return adapter.setAll(action.payload.items, {
                    ...state,
                    pagination: {...state.pagination, number: 0},
                    componentOpened: false
                });
            }

            return {...state, componentOpened: true};
        }
    }),

    on(ApartmentActions.LoadList, (state, action) => ({
        ...state,
        listLoading: StoredComponentHelperService.updateListLoading(action.payload, state.ids)
    })),

    on(ApartmentActions.LoadListSuccess, (state, action) => {
        return action.payload.clearState
            ? adapter.setAll(action.payload.items, {
                ...state,
                pagination: action.payload.pagination,
                listLoading: undefined

            })
            : adapter.upsertMany(action.payload.items, {
                ...state,
                pagination: action.payload.pagination,
                listLoading: undefined
            });
    }),

    on(ApartmentActions.LoadListFailure, (state) => ({
        ...state,
        listLoading: undefined
    })),

    on(ApartmentActions.SetFilter, (state, action) => ({
        ...state,
        filter: action.payload
    })),

    on(ApartmentActions.SetPagination, (state, action) => ({
        ...state,
        pagination: {...state.pagination, number: action.payload ? StoredComponentHelperService.setNextPage(state.pagination, state.ids?.length) : 0}
    })),

    on(ApartmentActions.SetLoading, (state, action) => ({
        ...state,
        listLoading: action.payload
    })),

    on(ApartmentActions.SetItemsLoading, (state, action) => ({
        ...state,
        itemsLoading: StoredComponentHelperService.updateItemLoading(state.itemsLoading, action.payload.id, action.payload.isStart)
    })),


    // TODO: OS CHECK Use comparator
    on(ApartmentActions.AddItemSuccess, (state, action) => {
        const savedItemAvailableByFilter = isAvailableByFilter(state.filter, action.payload);
        if (savedItemAvailableByFilter) {
            return adapter.setAll([action.payload, ...selectAllEntities(state)], {
                ...state,
                pagination: {...state.pagination, totalElements: StoredComponentHelperService.setTotalElements(action.payload.id!, state.pagination, state?.ids)}
            });
        }

        return {...state};
    }),

    on(ApartmentActions.UpdateItemSuccess, (state, action) => {
        const savedItemAvailableByFilter = isAvailableByFilter(state.filter, action.payload);
        if (savedItemAvailableByFilter) {
            return adapter.setOne(action.payload, {
                ...state,
                itemsLoading: StoredComponentHelperService.updateItemLoading(state.itemsLoading, action.payload.id!, false),
                pagination: {...state.pagination, totalElements: StoredComponentHelperService.setTotalElements(action.payload.id!, state.pagination, state?.ids)}
            });
        }

        return adapter.removeOne(action.payload.id!, {
            ...state,
            itemsLoading: StoredComponentHelperService.updateItemLoading(state.itemsLoading, action.payload.id!, false),
            pagination: {...state.pagination, totalElements: StoredComponentHelperService.setTotalElements(action.payload.id!, state.pagination, state?.ids, true)}
        });
    }),

    on(ApartmentActions.DeleteItem, (state, action) => ({
        ...state,
        itemsLoading: StoredComponentHelperService.updateItemLoading(state.itemsLoading, action.payload.id!, true)
    })),

    on(ApartmentActions.DeleteItemSuccess, (state, action) => {
        const updatedItem = {...action.payload!, archived: true};
        const savedItemAvailableByFilter = isAvailableByFilter(state.filter, updatedItem);
        if (savedItemAvailableByFilter) {
            return adapter.updateOne({id: action.payload.id!, changes: updatedItem}, {
                ...state,
                itemsLoading: StoredComponentHelperService.updateItemLoading(state.itemsLoading, action.payload.id!, false),
                pagination: {...state.pagination, totalElements: StoredComponentHelperService.setTotalElements(action.payload.id!, state.pagination, state?.ids)}
            });
        }

        return adapter.removeOne(action.payload.id!, {
            ...state,
            itemsLoading: StoredComponentHelperService.updateItemLoading(state.itemsLoading, action.payload.id!, false),
            pagination: {...state.pagination, totalElements: StoredComponentHelperService.setTotalElements(action.payload.id!, state.pagination, state?.ids, true)}
        });
    }),

    on(ApartmentActions.DeleteItemFailure, (state, action) => ({
        ...state,
        itemsLoading: StoredComponentHelperService.updateItemLoading(state.itemsLoading, action.payload.id!, false)
    })),

    on(ApartmentActions.SetInitialState, SetAllInitialStates, () => ({...initialState}))
);

export const {selectAll: selectAllEntities} = adapter.getSelectors();

const isAvailableByFilter = (searchParams: ApartmentsSearchParams | undefined, entity: ApartmentModel): boolean => {
    if (!searchParams?.includeDeleted && entity.archived) {
        return false;
    }

    if (!searchParams) {
        return true;
    }

    if (searchParams.building?.id && entity?.building?.id != searchParams.building.id) {
        return false;
    }

    if (searchParams.section?.id && entity?.section?.id != searchParams.section.id) {
        return false;
    }

    if (searchParams.floor != undefined && entity?.floor != searchParams.floor) {
        return false;
    }

    if (searchParams.apartmentTypes?.length &&
        !searchParams.apartmentTypes.some(
            type => type.id === entity?.type?.id
        )
    ) {
        return false;
    }

    if (searchParams.query && entity.name && entity.name.toLowerCase().indexOf(searchParams.query.toLowerCase()) == -1 && !searchByOwnerName(searchParams.query, entity)) {
        return false;
    }

    return !(searchParams.receipt != undefined && entity?.receipt != searchParams.receipt);
};

const searchByOwnerName = (query: string, entity: ApartmentModel): boolean => {
    for (let i = 0; i < entity?.owners?.length; i++) {
        if (entity.owners[i].name?.fullName && entity.owners[i].name.fullName!.toLowerCase().includes(query.toLowerCase())) {
            return true;
        }
    }
    if (entity?.others?.length) {
        for (let i = 0; i < entity?.others?.length; i++) {
            if (entity.others[i].name?.fullName && entity.others[i].name.fullName!.toLowerCase().includes(query.toLowerCase())) {
                return true;
            }
        }
    }

    return false;
};
